import { PipelineStageName } from '../../../modules/navigation/components/pipeline-navigation/pipeline-navigation.types';
import { BaseListServerResponse } from '../../../shared/domain/types/base.types';

export enum UserRole {
  REVI_EMPLOYEE = 'ReviEmployee',
  OWNER = 'Owner',
  ADMIN = 'Admin',
  MEMBER = 'Member',
}

export enum ClientType {
  CORPORATE_DEVELOPMENT = 'Corporate Development',
  PRIVATE_EQUITY = 'Private Equity',
  INVESTMENT_BANKS = 'Investment Banks',
}

export enum NotificationPreferences {
  none = 'none',
  immediate = 'immediate',
  daily = 'daily',
  weekly = 'weekly',
}

export interface IClient {
  id: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  companyName?: string;
  pipelineConfig: { skipStages?: PipelineStageName[] };
  notificationSettings: INotificationSettings;
  type: ClientType;
}

export interface INotificationSettings {
  email: string;
  preference: NotificationPreferences;
}

export interface IGetAllClientsItemDto {
  id: string;
  createdAt: string;
  companyName?: string;
}

export interface IThunkStatuses {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: any;
  isStarted: boolean;
  isFinished: boolean;
}

export interface IUserThunks {
  getAllClients: IThunkStatuses;
  getCurrentClient: IThunkStatuses;
  updateNotificationSettings: IThunkStatuses;
  getUser: IThunkStatuses;
}

export interface IPropelAuthOrgMemberInfo {
  orgId: string;
  orgName: string;
  orgMetadata: Record<string, any>;
  urlSafeOrgName: string;
  orgRoleStructure: string;
  userAssignedRole: string;
  userInheritedRolesPlusCurrentRole: string[];
  userPermissions: string[];
  userAssignedAdditionalRoles: string[];
}

export interface IPropelAuthUser {
  userId: string;
  email: string;
  orgIdToUserOrgInfo?: Record<string, IPropelAuthOrgMemberInfo>;
  loginMethod?: {
    loginMethod: string;
  };
  firstName?: string;
  lastName?: string;
  username?: string;
  createdAt?: number;
  properties?: Record<string, any>;
  pictureUrl?: string;
  hasPassword?: boolean;
  hasMfaEnabled?: boolean;
  canCreateOrgs?: boolean;
  legacyUserId?: string;
  impersonatorUserId?: string;
}

export type IUser = IPropelAuthUser & {
  client?: IClient;
};

export type UserState = {
  user: IUser;
  clients: IGetAllClientsItemDto[];
  selectedClients: any[];
  thunks: IUserThunks;
  currentClient?: IClient;
};

export interface IClientsListResponse extends BaseListServerResponse<IGetAllClientsItemDto> {}

export function getUserId(user: IUser): string {
  return user.userId;
}

export function getUserEmail(user: IUser): string {
  return user.email;
}

export function getUserDisplayName(user: IUser): string {
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || user.email.split('@')[0];
}

export function getUserRoles(user: IUser): UserRole[] {
  if (!user.orgIdToUserOrgInfo) return [];

  const roles: UserRole[] = [];
  Object.values(user.orgIdToUserOrgInfo).forEach(orgInfo => {
    // Add assigned role if it's a valid UserRole
    if (Object.values(UserRole).includes(orgInfo.userAssignedRole as UserRole)) {
      roles.push(orgInfo.userAssignedRole as UserRole);
    }

    // Add additional roles if they're valid UserRoles
    orgInfo.userAssignedAdditionalRoles.forEach(role => {
      if (Object.values(UserRole).includes(role as UserRole)) {
        roles.push(role as UserRole);
      }
    });
  });

  return [...new Set(roles)];
}

export function getUserAssignedClientId(user: IUser): string | null {
  return (user as any).client?.id || null;
}

export function hasSomeRoles(requiredRoles: UserRole[], userRoles: UserRole[]): boolean {
  return requiredRoles.some(role => userRoles.includes(role));
}
